import React, { useEffect, useMemo, useState } from "react";
import {
    Tags, Plus, Loader2, Inbox, X, Check, Edit2, Trash2,
    Lock, Building2, AlertCircle,
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { providerTypesApi } from "@/lib/api/providerTypes";
import { describeApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

// ── Form dialog (create + edit share the same shell) ───────────────────────
function ProviderTypeDialog({ open, mode, initial, onClose, onSaved }) {
    const { lang, isRTL } = useI18n();
    const tr = (en, ar) => (lang === "ar" ? ar : en);
    const isEdit = mode === "edit";

    const [form, setForm] = useState({
        code: "",
        name_en: "",
        name_ar: "",
        sort_order: 100,
        is_active: true,
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open) return;
        setForm({
            code: initial?.code || "",
            name_en: initial?.name_en || "",
            name_ar: initial?.name_ar || "",
            sort_order: initial?.sort_order ?? 100,
            is_active: initial ? !!initial.is_active : true,
        });
        setError("");
    }, [open, initial]);

    if (!open) return null;

    const update = (patch) => setForm((p) => ({ ...p, ...patch }));

    const submit = async (e) => {
        e.preventDefault();
        setError(""); setSubmitting(true);
        try {
            if (isEdit) {
                const payload = {
                    name_en: form.name_en.trim(),
                    name_ar: form.name_ar.trim(),
                    sort_order: Number(form.sort_order) || 100,
                    is_active: !!form.is_active,
                };
                const saved = await providerTypesApi.update(initial.id, payload);
                onSaved(saved);
            } else {
                const payload = {
                    code: form.code.trim().toUpperCase().replace(/[ -]+/g, "_"),
                    name_en: form.name_en.trim(),
                    name_ar: form.name_ar.trim(),
                    sort_order: Number(form.sort_order) || 100,
                    is_active: !!form.is_active,
                };
                const saved = await providerTypesApi.create(payload);
                onSaved(saved);
            }
            onClose();
        } catch (err) {
            setError(describeApiError(err));
        } finally { setSubmitting(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose} dir={isRTL ? "rtl" : "ltr"}>
            <div
                className="bg-card rounded-2xl border border-border shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                    <h2 className="text-base font-semibold">
                        {isEdit
                            ? tr("Edit provider type", "تعديل نوع مزود")
                            : tr("Add provider type", "إضافة نوع مزود")}
                    </h2>
                    <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1 rounded-lg hover:bg-secondary">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <form onSubmit={submit} className="p-5 space-y-3.5">
                    <div>
                        <Label>{tr("Code", "الكود")}</Label>
                        {isEdit ? (
                            <div className="mt-1.5 px-3 py-2.5 rounded-xl border border-border bg-muted/30 font-mono text-sm text-muted-foreground inline-flex items-center gap-2">
                                <Lock className="h-3.5 w-3.5" />
                                {form.code}
                            </div>
                        ) : (
                            <Input
                                dir="ltr"
                                required
                                autoFocus
                                value={form.code}
                                onChange={(e) => update({ code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "_") })}
                                placeholder="e.g. DENTAL_CLINIC"
                                className="mt-1.5 font-mono uppercase"
                                data-testid="pt-code"
                            />
                        )}
                        {!isEdit && (
                            <p className="mt-1 text-[11px] text-muted-foreground">
                                {tr(
                                    "Stable identifier, can't be changed later. Uppercase, alphanumeric, underscores allowed.",
                                    "معرف ثابت، لا يمكن تعديله لاحقاً. أحرف كبيرة وأرقام، يُسمح بشرطة سفلية."
                                )}
                            </p>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>{tr("Name (English)", "الاسم (إنجليزي)")}</Label>
                            <Input
                                dir="ltr"
                                required
                                value={form.name_en}
                                onChange={(e) => update({ name_en: e.target.value })}
                                placeholder="Dental Clinic"
                                className="mt-1.5"
                                data-testid="pt-name-en"
                            />
                        </div>
                        <div>
                            <Label>{tr("Name (Arabic)", "الاسم (عربي)")}</Label>
                            <Input
                                dir="rtl"
                                lang="ar"
                                required
                                value={form.name_ar}
                                onChange={(e) => update({ name_ar: e.target.value })}
                                placeholder="عيادة أسنان"
                                className="mt-1.5"
                                data-testid="pt-name-ar"
                            />
                        </div>
                    </div>

                    <div>
                        <Label>{tr("Sort order", "ترتيب العرض")}</Label>
                        <Input
                            type="number"
                            value={form.sort_order}
                            onChange={(e) => update({ sort_order: e.target.value })}
                            className="mt-1.5"
                        />
                        <p className="mt-1 text-[11px] text-muted-foreground">
                            {tr("Lower numbers appear first.", "الأرقام الأقل تظهر أولاً.")}
                        </p>
                    </div>

                    <div className="flex items-center gap-2 pt-1">
                        <input
                            id="pt-active"
                            type="checkbox"
                            checked={form.is_active}
                            onChange={(e) => update({ is_active: e.target.checked })}
                            className="h-4 w-4 accent-primary"
                        />
                        <label htmlFor="pt-active" className="text-sm">
                            {tr("Active — visible in dropdowns", "نشط — يظهر في القوائم")}
                        </label>
                    </div>

                    {error && (
                        <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm text-destructive flex items-start gap-2">
                            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="flex gap-2 pt-1">
                        <Button type="button" variant="outline" onClick={onClose}>
                            {tr("Cancel", "إلغاء")}
                        </Button>
                        <Button type="submit" disabled={submitting} className="flex-1" data-testid="pt-submit">
                            {submitting && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
                            {tr(isEdit ? "Save changes" : "Add type", isEdit ? "حفظ التغييرات" : "إضافة")}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ── Page ──────────────────────────────────────────────────────────────────
export default function ProviderTypesPage() {
    const { lang } = useI18n();
    const tr = (en, ar) => (lang === "ar" ? ar : en);

    const [items, setItems] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const res = await providerTypesApi.listAdmin({ include_inactive: true });
            setItems(res.items || []);
        } catch (err) {
            toast.error(describeApiError(err));
            setItems([]);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const openCreate = () => { setEditing(null); setDialogOpen(true); };
    const openEdit = (it) => { setEditing(it); setDialogOpen(true); };

    const handleDelete = async (it) => {
        if (it.is_system) {
            toast.error(tr("System types can't be deleted — deactivate them instead.", "أنواع النظام لا يمكن حذفها — قم بتعطيلها بدلاً من ذلك."));
            return;
        }
        if (it.provider_count > 0) {
            toast.error(tr(
                `${it.provider_count} provider(s) still use this type. Deactivate or migrate them first.`,
                `لا يزال ${it.provider_count} مزود يستخدم هذا النوع. قم بتعطيله أو نقلهم أولاً.`
            ));
            return;
        }
        if (!window.confirm(tr(`Delete provider type "${it.name_en}"?`, `حذف النوع "${it.name_ar || it.name_en}"؟`))) return;
        try {
            await providerTypesApi.delete(it.id);
            toast.success(tr("Deleted", "تم الحذف"));
            load();
        } catch (err) {
            toast.error(describeApiError(err));
        }
    };

    const toggleActive = async (it) => {
        try {
            await providerTypesApi.update(it.id, { is_active: !it.is_active });
            toast.success(tr(it.is_active ? "Deactivated" : "Activated", it.is_active ? "تم التعطيل" : "تم التفعيل"));
            load();
        } catch (err) {
            toast.error(describeApiError(err));
        }
    };

    return (
        <div className="max-w-4xl space-y-6 fade-up" data-testid="page-provider-types">
            <header className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <p className="text-sm font-medium text-primary">{tr("Administration", "الإدارة")}</p>
                    <h1 className="mt-1 text-3xl font-semibold tracking-tight inline-flex items-center gap-2">
                        <Tags className="h-7 w-7 text-primary" />
                        {tr("Provider types", "أنواع المزودين")}
                    </h1>
                    <p className="mt-1.5 text-sm text-muted-foreground">
                        {tr(
                            "The list shown to admins, sales agents, and providers when picking a clinic type. System defaults can be deactivated but not deleted.",
                            "القائمة المعروضة للمسؤولين ومندوبي المبيعات والمزودين عند اختيار نوع المنشأة. يمكن تعطيل الأنواع الافتراضية ولكن لا يمكن حذفها."
                        )}
                    </p>
                </div>
                <Button onClick={openCreate} className="rounded-xl">
                    <Plus className="h-4 w-4 me-1" />
                    {tr("Add type", "إضافة نوع")}
                </Button>
            </header>

            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
            ) : items.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border bg-card p-12 flex flex-col items-center gap-3 text-center">
                    <Inbox className="h-10 w-10 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">{tr("No provider types yet", "لا توجد أنواع")}</p>
                    <Button onClick={openCreate} size="sm">
                        <Plus className="h-3.5 w-3.5 me-1" />
                        {tr("Add the first one", "أضف الأول")}
                    </Button>
                </div>
            ) : (
                <div className="rounded-2xl border border-border bg-card overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/30 border-b border-border">
                                <tr className="text-start text-xs text-muted-foreground uppercase tracking-wide">
                                    <th className="px-4 py-3 text-start font-semibold">{tr("Code", "الكود")}</th>
                                    <th className="px-4 py-3 text-start font-semibold">{tr("Display name", "الاسم")}</th>
                                    <th className="px-4 py-3 text-end font-semibold">{tr("Order", "الترتيب")}</th>
                                    <th className="px-4 py-3 text-end font-semibold">{tr("Providers", "المزودون")}</th>
                                    <th className="px-4 py-3 text-center font-semibold">{tr("Status", "الحالة")}</th>
                                    <th className="px-4 py-3 text-end font-semibold"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border/50">
                                {items.map((it) => (
                                    <tr key={it.id} className={`hover:bg-muted/20 transition-colors ${!it.is_active ? "opacity-60" : ""}`}>
                                        <td className="px-4 py-3 font-mono text-xs">{it.code}</td>
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-foreground inline-flex items-center gap-1.5">
                                                {it.name_en}
                                                {it.is_system && (
                                                    <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground inline-flex items-center gap-1">
                                                        <Lock className="h-2.5 w-2.5" />
                                                        {tr("system", "نظام")}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-muted-foreground" dir="rtl">{it.name_ar}</div>
                                        </td>
                                        <td className="px-4 py-3 text-end tabular-nums text-muted-foreground">{it.sort_order}</td>
                                        <td className="px-4 py-3 text-end">
                                            {it.provider_count > 0 ? (
                                                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                                    <Building2 className="h-3 w-3" />
                                                    {it.provider_count}
                                                </span>
                                            ) : (
                                                <span className="text-xs text-muted-foreground">—</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <button
                                                type="button"
                                                onClick={() => toggleActive(it)}
                                                className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full transition-colors ${
                                                    it.is_active
                                                        ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                                        : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
                                                }`}
                                            >
                                                {it.is_active ? tr("Active", "نشط") : tr("Inactive", "معطل")}
                                            </button>
                                        </td>
                                        <td className="px-4 py-3 text-end">
                                            <div className="inline-flex items-center gap-1">
                                                <button
                                                    type="button"
                                                    onClick={() => openEdit(it)}
                                                    className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                                                    title={tr("Edit", "تعديل")}
                                                >
                                                    <Edit2 className="h-3.5 w-3.5" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDelete(it)}
                                                    disabled={it.is_system || it.provider_count > 0}
                                                    className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/5 transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-muted-foreground disabled:cursor-not-allowed"
                                                    title={
                                                        it.is_system
                                                            ? tr("System types can't be deleted", "أنواع النظام لا تُحذف")
                                                            : it.provider_count > 0
                                                                ? tr("Has providers attached", "يحتوي على مزودين")
                                                                : tr("Delete", "حذف")
                                                    }
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <ProviderTypeDialog
                open={dialogOpen}
                mode={editing ? "edit" : "create"}
                initial={editing}
                onClose={() => setDialogOpen(false)}
                onSaved={() => load()}
            />
        </div>
    );
}
